import styled from '@emotion/styled';
import CloseRounded from '@mui/icons-material/CloseRounded';
import LockOpenRounded from '@mui/icons-material/LockOpenRounded';
import { FormEvent, useEffect, useState } from 'react';
import { useKaraokeAuth } from '~/modules/Auth/KaraokeAuthContext';
import { KaraokeAuthError } from '~/modules/Auth/KaraokeAuthError';

export type AuthDialogMode = 'login' | 'register';

interface Props {
  mode: AuthDialogMode | null;
  onModeChange: (mode: AuthDialogMode) => void;
  onClose: () => void;
}

export default function KaraokeAuthModal({ mode, onModeChange, onClose }: Props) {
  const auth = useKaraokeAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const open = mode !== null;
  const isRegister = mode === 'register';

  useEffect(() => {
    if (!open) return;

    setFormError(null);
    setFieldErrors({});
    setPassword('');
    setPasswordConfirmation('');
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  if (!open || !mode) return null;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});

    try {
      if (isRegister) {
        await auth.register(email, password, passwordConfirmation);
      } else {
        await auth.login(email, password);
      }
      onClose();
    } catch (error) {
      if (error instanceof KaraokeAuthError) {
        setFormError(error.message);
        setFieldErrors(error.errors);
      } else {
        setFormError('Could not connect to fastKaraoke right now. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Backdrop role="presentation" onMouseDown={onClose}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="karaoke-auth-title"
        onMouseDown={(event) => event.stopPropagation()}>
        <CloseButton type="button" onClick={onClose} aria-label="Close login dialog">
          <CloseRounded aria-hidden="true" />
        </CloseButton>

        <IconCircle aria-hidden="true">
          <LockOpenRounded />
        </IconCircle>

        <Title id="karaoke-auth-title">{isRegister ? 'Create free account' : 'Welcome back'}</Title>
        <Intro>
          <strong>Create a FREE fastKaraoke account to play every song in the catalog.</strong>
        </Intro>

        <Form onSubmit={submit} noValidate>
          <Field>
            <span>Email</span>
            <input
              type="email"
              value={email}
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            {fieldErrors.email?.[0] && <FieldError>{fieldErrors.email[0]}</FieldError>}
          </Field>

          <Field>
            <span>Password</span>
            <input
              type="password"
              value={password}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            {fieldErrors.password?.[0] && <FieldError>{fieldErrors.password[0]}</FieldError>}
          </Field>

          {isRegister && (
            <Field>
              <span>Repeat password</span>
              <input
                type="password"
                value={passwordConfirmation}
                autoComplete="new-password"
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                required
              />
            </Field>
          )}

          {formError && <FormError>{formError}</FormError>}

          <PrimaryButton type="submit" disabled={submitting}>
            {submitting ? 'Please wait...' : isRegister ? 'Register for free' : 'Log in'}
          </PrimaryButton>
        </Form>

        <SwitchLine>
          {isRegister ? 'Already registered?' : 'New to fastKaraoke?'}{' '}
          <button type="button" onClick={() => onModeChange(isRegister ? 'login' : 'register')}>
            {isRegister ? 'Log in' : 'Create account'}
          </button>
        </SwitchLine>
      </Dialog>
    </Backdrop>
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(1, 8, 18, 0.68);
  backdrop-filter: blur(12px);
`;

const Dialog = styled.div`
  position: relative;
  width: min(440px, 100%);
  box-sizing: border-box;
  padding: 28px;
  border: 1px solid var(--walky-border);
  border-top: 4px solid var(--walky-orange);
  border-radius: 8px;
  background: var(--walky-surface-translucent);
  color: var(--walky-ink);
  box-shadow: 0 26px 80px rgba(0, 0, 0, 0.36);

  @media (max-width: 520px) {
    width: min(386px, 100%);
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
    padding: 22px 18px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 38px;
  height: 38px;
  min-width: 38px;
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--walky-border);
  border-radius: 8px;
  background: var(--walky-surface-raised);
  color: var(--walky-ink);
  cursor: pointer;
  box-shadow: var(--walky-shadow);

  svg {
    width: 21px;
    height: 21px;
    display: block;
    flex: 0 0 auto;
  }

  &:hover,
  &:focus-visible {
    color: #fff;
    background: var(--walky-orange);
    border-color: var(--walky-orange);
    outline: none;
  }

  @media (max-width: 520px) {
    top: 8px;
    right: 8px;
  }
`;

const IconCircle = styled.div`
  width: 46px;
  height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #fff;
  background: linear-gradient(135deg, var(--walky-orange), #ff7a1a);
  box-shadow: 0 14px 30px rgba(244, 90, 10, 0.24);

  svg {
    width: 25px;
    height: 25px;
  }
`;

const Title = styled.h2`
  margin: 18px 0 0;
  font-size: clamp(28px, 4vw, 38px);
  line-height: 1;
  letter-spacing: 0;
`;

const Intro = styled.p`
  margin: 10px 0 0;
  color: var(--walky-muted);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.45;

  strong {
    color: var(--walky-ink);
    font-weight: 900;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 14px;
  margin-top: 22px;
`;

const Field = styled.label`
  display: grid;
  gap: 7px;
  color: var(--walky-muted);
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;

  input {
    width: 100%;
    min-height: 46px;
    box-sizing: border-box;
    border: 1px solid var(--walky-border);
    border-radius: 8px;
    background: var(--walky-canvas);
    color: var(--walky-ink);
    padding: 0 13px;
    font-size: 16px;
    font-weight: 700;
    outline: none;
  }

  input:focus {
    border-color: var(--walky-orange);
    box-shadow: 0 0 0 3px rgba(244, 90, 10, 0.16);
  }
`;

const FieldError = styled.small`
  color: #ffb199;
  font-size: 12px;
  line-height: 1.3;
  text-transform: none;
`;

const FormError = styled.div`
  padding: 10px 12px;
  border: 1px solid rgba(255, 115, 91, 0.36);
  border-radius: 8px;
  color: #ffd8cf;
  background: rgba(114, 29, 29, 0.28);
  font-size: 13px;
  font-weight: 800;
`;

const PrimaryButton = styled.button`
  min-height: 48px;
  border: 1px solid rgba(244, 90, 10, 0.5);
  border-radius: 8px;
  background: linear-gradient(135deg, var(--walky-orange), #ff7a1a);
  color: #fff;
  cursor: pointer;
  font-size: 15px;
  font-weight: 900;
  text-transform: uppercase;
  box-shadow: 0 14px 28px rgba(244, 90, 10, 0.2);

  &:disabled {
    cursor: progress;
    opacity: 0.72;
  }

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    filter: saturate(1.06) brightness(1.02);
    outline: none;
  }
`;

const SwitchLine = styled.div`
  margin-top: 18px;
  color: var(--walky-muted);
  text-align: center;
  font-size: 14px;
  font-weight: 700;

  button {
    border: 0;
    background: transparent;
    color: var(--walky-orange);
    cursor: pointer;
    font: inherit;
    font-weight: 900;
    padding: 0;
  }

  button:hover,
  button:focus-visible {
    text-decoration: underline;
    outline: none;
  }
`;
